import React from "react";
import {
    Box,
    Paper,
    Stack,
    Chip,
    Button,
    Typography,
    useMediaQuery, Pagination,
} from "@mui/material";
import TableLink from "./tableLink";

function getStatusColors(type) {
    switch (type) {
        case "processing":
            return {bg: "#E8BAD4", color: "#111111"};
        case "cancelled":
            return {bg: "#8D8D8D", color: "#FFFFFF"};
        case "completed":
        default:
            return {bg: "#E5007E", color: "#FFFFFF"};
    }
}

export default function OrdersTable({
                                        orders = [],
                                        onViewOrder,
                                        onViewInvoice, t
                                    }) {
    const isMobile = useMediaQuery("(max-width: 600px)");
    const [page, setPage] = React.useState(1);
    const rowsPerPage = 3;



    const pageCount = Math.max(
        1,
        Math.ceil(orders.length / rowsPerPage || 1)
    );

    React.useEffect(() => {
        if (page > pageCount) {
            setPage(pageCount);
        }
    }, [page, pageCount]);

    const paginatedOrders = React.useMemo(() => {
        const start = (page - 1) * rowsPerPage;
        const end = start + rowsPerPage;
        return orders.slice(start, end);
    }, [orders, page]);

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    return (
        <Box sx={{overflowX: {xs: "visible", md: "auto"}}}>
            <Paper
                elevation={0}
                sx={{
                    borderRadius: "20px",
                    backgroundColor: "#F5F5F5",
                    p: 0,
                    minWidth: {md: 900},
                }}
            >

                {!isMobile && (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "2fr 2fr 2fr 2fr 1.5fr 1.5fr",
                            px: 4,
                            py: 2,
                            borderBottom: "1px solid #E0E0E0",
                            fontFamily: "'Roboto Condensed', sans-serif",
                            fontSize: 14,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            color: "#111111",
                        }}
                    >
                        <Box>{t('Order Number')}</Box>
                        <Box>{t('Date')}</Box>
                        <Box>{t('Status')}</Box>
                        <Box>{t('Total')}</Box>
                    </Box>
                )}


                <Stack spacing={1.2} sx={{p: {xs: 1, md: 2}}}>
                    {paginatedOrders.map((order) => {
                        const statusColors = getStatusColors(order.statusType);
                        const canShowInvoice =
                            order.statusType === "completed";

                        return (
                            <Paper
                                key={order.id}
                                elevation={0}
                                sx={{
                                    borderRadius: "10px",
                                    backgroundColor: "#E9E9E9",
                                    px: {xs: 2, md: 3},
                                    py: 2,
                                }}
                            >

                                {!isMobile && (
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns:
                                                "2fr 2fr 2fr 2fr 1.5fr 1.5fr",
                                            alignItems: "center",
                                            columnGap: 2,
                                            fontFamily:
                                                "'Roboto Condensed', sans-serif",
                                            fontSize: 14,
                                            color: "#111111",
                                        }}
                                    >
                                        <Box>{order.raw.orderNumber}</Box>
                                        <Box>{order.date}</Box>

                                        <Chip
                                            label={order.status}
                                            size="small"
                                            sx={{
                                                px: 2,
                                                fontSize: 12,
                                                backgroundColor: statusColors.bg,
                                                color: statusColors.color,
                                                borderRadius: "20px",
                                                fontWeight: 700,
                                            }}
                                        />

                                        <Box>{order.total}</Box>

                                        <TableLink onClick={() => onViewOrder && onViewOrder(order)}>
                                            {t('View')}
                                        </TableLink>

                                    </Box>
                                )}
                                {isMobile && (
                                    <Stack
                                        spacing={1.2}
                                        sx={{
                                            fontFamily:
                                                "'Roboto Condensed', sans-serif",
                                            fontSize: 14,
                                            color: "#111111",
                                        }}
                                    >
                                        <Row label={t('Order Number')} value={order.id}/>
                                        <Row label={t('Date')} value={order.date}/>
                                        <Row
                                            label={t('Status')}
                                            value={
                                                <Chip
                                                    label={order.status}
                                                    size="small"
                                                    sx={{
                                                        px: 1.2,
                                                        py: 0,
                                                        height: 24,
                                                        fontSize: 11,
                                                        backgroundColor: statusColors.bg,
                                                        color: statusColors.color,
                                                        borderRadius: "12px",
                                                        fontWeight: 700,
                                                    }}
                                                />

                                            }
                                        />
                                        <Row label={t('Total')} value={order.total}/>

                                        <Row
                                            value={
                                                <TableLink onClick={() => onViewOrder && onViewOrder(order)}>
                                                    {t('View')}
                                                </TableLink>
                                            }
                                        />
                                    </Stack>
                                )}
                            </Paper>
                        );
                    })}
                </Stack>
                {orders.length > rowsPerPage && (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            py: 2,
                            pb: 3,
                        }}
                    >
                        <Pagination
                            count={pageCount}
                            page={page}
                            onChange={handlePageChange}
                            color="primary"
                            size={isMobile ? "small" : "medium"}
                        />
                    </Box>
                )}
            </Paper>
        </Box>
    );
}


function Row({label, value}) {
    return (
        <Box sx={{display: "flex", justifyContent: "space-between"}}>
            <Typography sx={{fontWeight: 700}}>{label}</Typography>
            <Box>{value}</Box>
        </Box>
    );
}
